import {
	createContext,
	useContext,
	useRef,
	useState,
	useCallback,
	type ReactNode,
} from "react";
import { useApi } from "@/shared/utils/request";
import { useData } from "@/v2/contexts/DataProvider";
import { ActionQueue, type QueuedAction } from "@/v2/hooks/useActionQueue";
import { deleteAction } from "@/v2/hooks/actions/delete";
import { editAction, type EditPayload } from "@/v2/hooks/actions/edit";
import { addAction, type AddPayload } from "@/v2/hooks/actions/add";
import { duplicateAction } from "@/v2/hooks/actions/duplicate";
import { combineAction } from "@/v2/hooks/actions/combine";
import { exportAction, executeExport, type ExportResult } from "@/v2/hooks/actions/export";
import { resolveFeedbackAction } from "@/v2/hooks/actions/resolveFeedback";
import { apiCall, type ActionDeps } from "@/v2/hooks/actions/types";

class ExportAfterEditError extends Error {}

// ---------------------------------------------------------------------------
// Context type
// ---------------------------------------------------------------------------

type ActionsContextType = {
	deleteEntries: (ids: number[]) => void;
	editEntry: (entry: EditPayload) => void;
	editThenExport: (entry: EditPayload) => void;
	addEntry: (entry: AddPayload, onSuccess?: () => void) => void;
	duplicateEntries: (ids: number[]) => void;
	combineEntries: (ids: number[], matterId: string, mainEntryId: number) => void;
	exportEntries: (ids: number[]) => void;
	resolveFeedback: (entryId: number) => void;
	enqueue: (action: QueuedAction) => void;
	failedExports: Map<number, string>;
	pendingEntryIds: Set<number>;
	queueSize: number;
};

const ActionsContext = createContext<ActionsContextType | undefined>(undefined);

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

export function ActionQueueProvider({ children }: { children: ReactNode }) {
	const { makeApiRequest } = useApi();
	const { updateEntries, refreshRecords } = useData();
	const [queueSize, setQueueSize] = useState(0);
	const [failedExports, setFailedExports] = useState<Map<number, string>>(new Map());
	const [pendingEntryIds, setPendingEntryIds] = useState<Set<number>>(new Set());

	const trackPending = useCallback(
		(ids: number[], action: QueuedAction): QueuedAction => {
			setPendingEntryIds((prev) => {
				const next = new Set(prev);
				for (const id of ids) next.add(id);
				return next;
			});
			const removePending = () =>
				setPendingEntryIds((prev) => {
					const next = new Set(prev);
					for (const id of ids) next.delete(id);
					return next;
				});
			return {
				...action,
				onSuccess: () => { removePending(); action.onSuccess?.(); },
				onError: () => { removePending(); action.onError?.(); },
			};
		},
		[],
	);

	// Keep deps in a ref so the queue always uses the latest closures
	const depsRef = useRef<ActionDeps>({ makeApiRequest, updateEntries, refreshRecords });
	depsRef.current = { makeApiRequest, updateEntries, refreshRecords };

	// Stable deps proxy that always reads from the ref
	const stableDeps = useRef<ActionDeps>({
		makeApiRequest: (opts) => depsRef.current.makeApiRequest(opts),
		updateEntries: (updater) => depsRef.current.updateEntries(updater),
		refreshRecords: () => depsRef.current.refreshRecords(),
	}).current;

	// Lazily create the queue once
	const queueRef = useRef<ActionQueue | null>(null);
	if (!queueRef.current) {
		queueRef.current = new ActionQueue(setQueueSize);
	}

	const enqueue = useCallback((action: QueuedAction) => {
		queueRef.current!.enqueue(action);
	}, []);

	// --- Export completion handler ---

	const handleExportComplete = useCallback((result: ExportResult) => {
		setFailedExports((prev) => {
			const next = new Map(prev);
			// Clear successes
			for (const id of result.exportedIds) next.delete(id);
			// Add failures
			for (const { id, error } of result.failedEntries) next.set(id, error);
			return next;
		});
	}, []);

	const recordExportFailure = useCallback((id: number, error: string) => {
		setFailedExports((prev) => {
			const next = new Map(prev);
			next.set(id, error);
			return next;
		});
	}, []);

	// --- Clear failed export when entry is edited or deleted ---

	const clearFailedExports = useCallback((ids: number[]) => {
		setFailedExports((prev) => {
			if (!ids.some((id) => prev.has(id))) return prev;
			const next = new Map(prev);
			for (const id of ids) next.delete(id);
			return next;
		});
	}, []);

	// --- Dispatch helpers ---

	const deleteEntries = useCallback(
		(ids: number[]) => {
			clearFailedExports(ids);
			enqueue(trackPending(ids, deleteAction(ids, stableDeps)));
		},
		[enqueue, stableDeps, clearFailedExports, trackPending],
	);

	const editEntry = useCallback(
		(entry: EditPayload) => {
			clearFailedExports([entry.id]);
			enqueue(trackPending([entry.id], editAction(entry, stableDeps)));
		},
		[enqueue, stableDeps, clearFailedExports, trackPending],
	);

	const editThenExport = useCallback(
		(entry: EditPayload) => {
			const id = entry.id;
			clearFailedExports([id]);
			const undoOptimistic = stableDeps.updateEntries((prev) =>
				prev.map((e) => (e.id === id ? { ...e, ...entry } : e)),
			);
			const actionId = crypto.randomUUID();
			let editSucceeded = false;
			enqueue(trackPending([id], {
				id: actionId,
				execute: async () => {
					await apiCall(stableDeps, {
						endpoint: "/records/edit",
						method: "POST",
						body: entry,
					});
					editSucceeded = true;
					try {
						await executeExport(actionId, [id], stableDeps, handleExportComplete, recordExportFailure);
					} catch (err) {
						throw new ExportAfterEditError(err instanceof Error ? err.message : "Export failed");
					}
				},
				rollback: () => { if (!editSucceeded) undoOptimistic(); },
				toast: {
					loading: "Saving and exporting…",
					success: "Entry saved and exported",
					error: (err) =>
						err instanceof ExportAfterEditError
							? "Entry saved but export failed"
							: "Failed to save entry",
				},
				onSuccess: () => stableDeps.refreshRecords(),
				onError: () => stableDeps.refreshRecords(),
			}));
		},
		[enqueue, stableDeps, clearFailedExports, handleExportComplete, recordExportFailure, trackPending],
	);

	const addEntry = useCallback(
		(entry: AddPayload, onSuccess?: () => void) =>
			enqueue(addAction(entry, stableDeps, onSuccess, recordExportFailure)),
		[enqueue, stableDeps, recordExportFailure],
	);

	const duplicateEntries = useCallback(
		(ids: number[]) => enqueue(trackPending(ids, duplicateAction(ids, stableDeps))),
		[enqueue, stableDeps, trackPending],
	);

	const combineEntries = useCallback(
		(ids: number[], matterId: string, mainEntryId: number) =>
			enqueue(trackPending(ids, combineAction(ids, matterId, mainEntryId, stableDeps))),
		[enqueue, stableDeps, trackPending],
	);

	const exportEntries = useCallback(
		(ids: number[]) =>
			enqueue(trackPending(ids, exportAction(ids, stableDeps, handleExportComplete, recordExportFailure))),
		[enqueue, stableDeps, handleExportComplete, recordExportFailure, trackPending],
	);

	const resolveFeedback = useCallback(
		(entryId: number) =>
			enqueue(trackPending([entryId], resolveFeedbackAction(entryId, stableDeps))),
		[enqueue, stableDeps, trackPending],
	);

	return (
		<ActionsContext.Provider
			value={{
				deleteEntries,
				editEntry,
				editThenExport,
				addEntry,
				duplicateEntries,
				combineEntries,
				exportEntries,
				resolveFeedback,
				enqueue,
				failedExports,
				pendingEntryIds,
				queueSize,
			}}
		>
			{children}
		</ActionsContext.Provider>
	);
}

// ---------------------------------------------------------------------------
// Consumer hook
// ---------------------------------------------------------------------------

export function useActions() {
	const ctx = useContext(ActionsContext);
	if (!ctx)
		throw new Error("useActions must be used within ActionQueueProvider");
	return ctx;
}
